import { useState } from "react";
import { uitgaandeLot } from "@/lib/lotcode";

// Instellingen voor de uitgaande (klant) lotcode. Diepvries = THT (productiedatum + maanden),
// Vers = dagcode (werkdagen vanaf een anker). Alles wordt deterministisch afgeleid, dus niets
// wordt overschreven of verwijderd.
export function LotcodeSettings({ lotConfig, setLotConfig, products }) {
  const cfg = lotConfig || {};
  const [open, setOpen] = useState(false);
  const set = (patch) => setLotConfig({ ...cfg, ...patch });
  const versSet = new Set(Array.isArray(cfg.versProducten) ? cfg.versProducten : []);
  const toggleVers = (naam) => { const s = new Set(versSet); if (s.has(naam)) s.delete(naam); else s.add(naam); set({ versProducten: [...s] }); };
  const setTht = (naam, val) => { const m = { ...(cfg.thtPerProduct || {}) }; if (val === "" || val == null) delete m[naam]; else m[naam] = Number(val); set({ thtPerProduct: m }); };
  const inp = "rounded border border-slate-300 px-2 py-1 text-sm";
  const today = new Date().toISOString().slice(0, 10);
  const namen = [...new Set((products || []).map((p) => p.naam).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]" data-testid="lotcode-settings">
      <button onClick={() => setOpen((o) => !o)} data-testid="lotcode-toggle" className="flex w-full items-center justify-between px-5 py-4 text-left">
        <div>
          <div className="text-lg font-semibold text-slate-900">Uitgaande lotcodes (klant)</div>
          <div className="text-xs text-slate-500">Bepaalt de lot/batchcode op de doos naar de klant. Diepvries = THT-datum · Vers = dagcode.</div>
        </div>
        <span className="text-sm font-semibold text-[#1C64B0]">{open ? "Verbergen" : "Instellen"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="text-sm">Standaard THT (maanden)
              <input type="number" step="1" data-testid="lotcode-tht-default" className={`${inp} mt-1 w-full`} value={cfg.defaultThtMaanden ?? 18} onChange={(e) => set({ defaultThtMaanden: e.target.value === "" ? "" : Number(e.target.value) })} />
            </label>
            <label className="text-sm">Verse dagcode — ankerdatum
              <input type="date" data-testid="lotcode-anchor-datum" className={`${inp} mt-1 w-full`} value={cfg.versAnchorDatum || ""} onChange={(e) => set({ versAnchorDatum: e.target.value })} />
            </label>
            <label className="text-sm">Verse dagcode — code op die dag
              <input type="number" step="1" data-testid="lotcode-anchor-code" className={`${inp} mt-1 w-full`} placeholder="bv. 20400" value={cfg.versAnchorCode ?? ""} onChange={(e) => set({ versAnchorCode: e.target.value === "" ? "" : Number(e.target.value) })} />
            </label>
          </div>
          <p className="text-xs text-slate-400">Voorbeeld: productiedatum {fmtDemo(today)} → diepvries-tracecode <b>{demoCode(today, cfg)}</b>. Vul bij "verse dagcode" één bekende dag + code in (bv. vandaag = 20400); de app rekent werkdagen door (weekend telt niet mee).</p>

          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Per product (standaard = diepvries, {cfg.defaultThtMaanden ?? 18} mnd THT)</div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50"><tr>{["Product", "Vers?", "THT (mnd) afwijkend", "Voorbeeld tracecode"].map((c, i) => <th key={i} className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{c}</th>)}</tr></thead>
                <tbody>
                  {namen.length === 0 ? <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Geen producten</td></tr> : namen.map((naam) => {
                    const isVers = versSet.has(naam);
                    const ex = uitgaandeLot(naam, today, cfg);
                    return (
                      <tr key={naam} className="border-t border-slate-100" data-testid="lotcode-prod-row">
                        <td className="px-3 py-2 text-slate-700">{naam}</td>
                        <td className="px-3 py-2"><input type="checkbox" data-testid="lotcode-vers" className="h-4 w-4 accent-[#1C64B0]" checked={isVers} onChange={() => toggleVers(naam)} /></td>
                        <td className="px-3 py-2">{isVers ? <span className="text-slate-300">n.v.t.</span> : <input type="number" step="1" data-testid="lotcode-tht" className={`${inp} w-20`} placeholder={String(cfg.defaultThtMaanden ?? 18)} value={(cfg.thtPerProduct || {})[naam] ?? ""} onChange={(e) => setTht(naam, e.target.value)} />}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{ex ? `${ex.type} · ${ex.tracecode || "(stel anker in)"}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDemo(iso) { const p = String(iso).split("-"); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : iso; }
function demoCode(iso, cfg) { const u = uitgaandeLot("__demo__", iso, cfg); return u ? u.tracecode : "—"; }
